import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Check, Sparkles, Clock, Loader2, GitBranch, Network } from "lucide-react";
import Seo from "@/components/Seo";
import { AdBanner } from "@/components/AdBanner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function StoryPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [lens, setLens] = useState("neutral");
  const [lensText, setLensText] = useState({});
  const [lensLoading, setLensLoading] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null); setError(false); setLens("neutral"); setLensText({}); setThread(null);
    api.get(`/news/item/${id}`)
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => active && setError(true));
    return () => { active = false; };
  }, [id]);

  const loadThread = async () => {
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/news/item/${id}/thread`);
      setThread(data);
    } catch {
      setThread({ text: "Couldn't connect the dots right now. Try again.", sources: [] });
    } finally {
      setThreadLoading(false);
    }
  };

  const loadLens = async (lensId) => {
    setLens(lensId);
    if (lensText[lensId] || lensId === "neutral") return;
    setLensLoading(true);
    try {
      const { data } = await api.get(`/news/item/${id}/lens/${lensId}`);
      setLensText((p) => ({ ...p, [lensId]: data.text }));
    } catch {
      setLensText((p) => ({ ...p, [lensId]: "Couldn't generate this perspective right now. Try again." }));
    } finally {
      setLensLoading(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="font-display font-black text-3xl uppercase">Story not found</p>
        <Link to="/news" className="text-neon-cyan hover:underline mt-4 inline-block">← Back to news</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 flex flex-col items-center gap-4 text-zinc-400" data-testid="story-loading">
        <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
        <p className="font-mono text-sm">Synthesizing story…</p>
      </div>
    );
  }

  const { item, ai, timeline, lenses } = data;
  const gamingLenses = lenses.filter((l) => l.group === "gaming");
  const politicalLenses = lenses.filter((l) => l.group === "political");

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="story-page">
      <Seo title={item.title} description={ai?.summary || item.excerpt} />

      <Link to="/news" className="inline-flex items-center gap-1 text-zinc-400 hover:text-neon-cyan text-sm font-semibold uppercase tracking-widest mb-6">
        <ArrowLeft className="w-4 h-4" /> Gaming News
      </Link>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-neon-cyan text-black rounded">{item.source}</span>
        <span className="text-xs text-zinc-500 font-mono">{fmtDate(item.published)}</span>
      </div>

      <h1 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tighter leading-[1.05]">{item.title}</h1>

      <img src={item.image} alt={item.title} className="w-full aspect-video object-cover rounded-2xl mt-6 border-2 border-zinc-800" />

      {/* AI synthesis */}
      <div className="mt-8 rounded-2xl border-2 border-neon-cyan/40 bg-neon-cyan/5 p-6" data-testid="ai-summary">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-neon-cyan" />
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-neon-cyan">AI Brief</h2>
        </div>
        <p className="text-zinc-200 leading-relaxed">{ai?.summary}</p>
        {ai?.key_points?.length > 0 && (
          <ul className="space-y-2 mt-4">
            {ai.key_points.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-zinc-300 text-sm">
                <Check className="w-4 h-4 text-neon-lime shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        )}
        {ai?.why_it_matters && (
          <p className="mt-4 text-sm text-zinc-400"><span className="text-neon-lime font-bold uppercase tracking-widest text-xs">Why it matters · </span>{ai.why_it_matters}</p>
        )}
      </div>

      {/* Lens switcher */}
      <div className="mt-10" data-testid="lens-switcher">
        <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-1">Read it through a lens</h2>
        <p className="text-zinc-500 text-sm mb-5">Same facts, different perspective. AI reframes the story from each viewpoint.</p>

        <div className="space-y-4">
          <LensRow label="Gaming" lenses={gamingLenses} active={lens} onPick={loadLens} />
          <LensRow label="Socio-Political" lenses={politicalLenses} active={lens} onPick={loadLens} />
        </div>

        <div className="mt-6 rounded-2xl border-2 border-zinc-800 bg-zinc-950 p-6 min-h-[140px]" data-testid="lens-output">
          {lens === "neutral" ? (
            <p className="text-zinc-200 leading-relaxed">{ai?.summary}</p>
          ) : lensLoading && !lensText[lens] ? (
            <div className="flex items-center gap-2 text-zinc-400 font-mono text-sm"><Loader2 className="w-4 h-4 animate-spin text-neon-pink" /> Generating perspective…</div>
          ) : (
            <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{lensText[lens]}</p>
          )}
        </div>
      </div>

      <a href={item.link} target="_blank" rel="noopener noreferrer" data-testid="read-source"
        className="inline-flex items-center gap-2 mt-6 text-neon-cyan hover:text-white font-bold uppercase text-sm tracking-widest">
        Read original at {item.source} <ExternalLink className="w-4 h-4" />
      </a>

      <AdBanner slot="story" className="my-10" />

      {/* The Bigger Picture — vector-RAG synthesis */}
      {data.has_thread && (
        <section className="mb-10" data-testid="story-thread">
          <div className="rounded-2xl border-2 border-neon-pink/40 bg-neon-pink/5 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Network className="w-5 h-5 text-neon-pink" />
              <h2 className="font-display font-black text-xl uppercase tracking-tight">The Bigger Picture</h2>
            </div>
            {!thread && !threadLoading && (
              <>
                <p className="text-zinc-400 text-sm mb-4">Our AI scans related coverage across every outlet and connects the dots into one evolving narrative.</p>
                <Button onClick={loadThread} data-testid="connect-dots-btn"
                  className="bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest h-11 px-6 hover:bg-white hover:text-black">
                  <Network className="w-4 h-4 mr-2" /> Connect the dots
                </Button>
              </>
            )}
            {threadLoading && (
              <div className="flex items-center gap-2 text-zinc-400 font-mono text-sm"><Loader2 className="w-4 h-4 animate-spin text-neon-pink" /> Retrieving & synthesizing…</div>
            )}
            {thread && (
              <>
                <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap" data-testid="thread-text">{thread.text}</p>
                {thread.sources?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {thread.sources.map((src) => (
                      <Link key={src.id} to={`/news/story/${src.id}`} className="text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border border-zinc-700 text-zinc-300 hover:border-neon-pink hover:text-neon-pink transition-colors">
                        {src.source}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Living timeline */}
      {timeline?.length > 0 && (
        <section className="mt-6" data-testid="story-timeline">
          <div className="flex items-center gap-2 mb-5">
            <GitBranch className="w-5 h-5 text-neon-lime" />
            <h2 className="font-display font-black text-2xl uppercase tracking-tight">How this story developed</h2>
          </div>
          <div className="relative border-l-2 border-zinc-800 ml-2 space-y-5">
            {timeline.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="relative pl-6">
                <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-neon-lime" />
                <Link to={`/news/story/${t.id}`} className="group block" data-testid={`timeline-item-${i}`}>
                  <span className="text-xs text-zinc-500 font-mono">{fmtDate(t.published)} · {t.source}</span>
                  <h3 className="font-display font-bold text-base leading-tight group-hover:text-neon-cyan transition-colors">{t.title}</h3>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const LensRow = ({ label, lenses, active, onPick }) => (
  <div>
    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {lenses.map((l) => (
        <button key={l.id} onClick={() => onPick(l.id)} data-testid={`lens-${l.id}`}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-full border-2 transition-colors ${
            active === l.id ? "bg-neon-pink text-white border-neon-pink" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-white"
          }`}>
          {l.label}
        </button>
      ))}
    </div>
  </div>
);
